export interface InternalContentTextPart {
    type: "text";
    value: string;
}

export interface InternalContentLinkPart {
    type: "link";
    label: string;
    href: string;
}

export type InternalContentPart = InternalContentTextPart | InternalContentLinkPart;

const LINK_PATTERN = /\[([^\]\r\n]+)\]\(([^)\r\n]+)\)/g;

/**
 * Only root-relative links are supported in admin-authored collection copy.
 * This keeps the feature useful for internal policy and information pages
 * without turning the field into an arbitrary HTML or external-link surface.
 */
export const isSafeInternalHref = (href: string): boolean =>
    href.startsWith("/") && !href.startsWith("//") && !/[\u0000-\u001f\u007f\s]/.test(href);

export function parseInternalContentLinks(value: string): InternalContentPart[] {
    const parts: InternalContentPart[] = [];
    let cursor = 0;
    const appendText = (text: string) => {
        if (!text) return;
        const previous = parts.at(-1);
        if (previous?.type === "text") {
            previous.value += text;
        } else {
            parts.push({ type: "text", value: text });
        }
    };

    for (const match of value.matchAll(LINK_PATTERN)) {
        const index = match.index;
        const fullMatch = match[0];
        const label = match[1]?.trim() || "";
        const href = match[2]?.trim() || "";

        if (index > cursor) {
            appendText(value.slice(cursor, index));
        }

        if (label && isSafeInternalHref(href)) {
            parts.push({ type: "link", label, href });
        } else {
            appendText(fullMatch);
        }

        cursor = index + fullMatch.length;
    }

    if (cursor < value.length) {
        appendText(value.slice(cursor));
    }

    return parts.length > 0 ? parts : [{ type: "text", value }];
}

/** Returns the shopper-visible link label for JSON-LD and other plain-text contexts. */
export function stripInternalContentLinkMarkup(value: string): string {
    return parseInternalContentLinks(value)
        .map((part) => (part.type === "link" ? part.label : part.value))
        .join("");
}

/**
 * Converts the supported Markdown subset to readable text for JSON-LD and
 * summaries. This is not used for HTML rendering.
 */
export function markdownContentToPlainText(value: string): string {
    return value
        .replace(/!\[([^\]\r\n]*)\]\([^)\r\n]+\)/g, "$1")
        .replace(/\[([^\]\r\n]+)\]\([^)\r\n]+\)/g, "$1")
        .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
        .replace(/^[ \t]{0,3}>[ \t]?/gm, "")
        .replace(/^[ \t]{0,3}(?:[-+*]|\d+[.)])[ \t]+/gm, "")
        .replace(/(\*\*|__)(.*?)\1/g, "$2")
        .replace(/([*_])([^*_\r\n]+)\1/g, "$2")
        .replace(/`([^`\r\n]+)`/g, "$1")
        .replace(/<[^>]*>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}
